import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';
import { getConfig } from '../runtimeConfig';
import { cachedFetch } from '../cacheManager';

const API = getConfig().REACT_APP_API;

interface ExpiryStoreDashboardProps {
  data?: any[];
  stats?: any;
  storageAgeDistribution?: any[];
  expiryDistribution?: any[];
  loading?: boolean;
}

// UNIFIED PROFESSIONAL COLOR PALETTE - Light & Cohesive
const COLORS = {
  primary: '#3b82f6',       // Soft Blue
  secondary: '#8b5cf6',     // Soft Purple
  success: '#10b981',       // Soft Green
  warning: '#f59e0b',       // Soft Amber
  danger: '#ef4444',        // Soft Red
  info: '#06b6d4',          // Soft Cyan
  accent: '#ec4899',        // Soft Pink
  gradient1: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  gradient2: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
  gradient3: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  gradient4: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  gradient5: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  gradient6: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
};

const EXPIRY_COLORS = [COLORS.success, COLORS.primary, COLORS.info, COLORS.warning, COLORS.danger, COLORS.accent];
const STORAGE_COLORS = [COLORS.info, COLORS.secondary, COLORS.warning, COLORS.danger];

const ExpiryStoreDashboard: React.FC<ExpiryStoreDashboardProps> = ({
  data: propData,
  stats: propStats,
  storageAgeDistribution: propStorageAgeDistribution,
  expiryDistribution: propExpiryDistribution,
  loading: propLoading
}) => {
  // Internal state for API data
  const [internalData, setInternalData] = useState<any[]>([]);
  const [internalStats, setInternalStats] = useState<any>(null);
  const [internalStorageAgeDistribution, setInternalStorageAgeDistribution] = useState<any[]>([]);
  const [internalExpiryDistribution, setInternalExpiryDistribution] = useState<any[]>([]);
  const [internalLoading, setInternalLoading] = useState(true);
  const [allCustomers, setAllCustomers] = useState<string[]>([]);
  const [customerStats, setCustomerStats] = useState<Record<string, { expired: number; expiring30: number }>>({});

  // Use props if provided, otherwise use internal state
  const data = propData && propData.length > 0 ? propData : internalData;
  const stats = propStats || internalStats;
  const storageAgeDistribution = propStorageAgeDistribution && propStorageAgeDistribution.length > 0 ? propStorageAgeDistribution : internalStorageAgeDistribution;
  const expiryDistribution = propExpiryDistribution && propExpiryDistribution.length > 0 ? propExpiryDistribution : internalExpiryDistribution;
  // Use propLoading only if props have data, otherwise use internal loading state
  const loading = propData && propData.length > 0 ? (propLoading || false) : internalLoading;

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedStorageAge, setSelectedStorageAge] = useState('');
  const [selectedExpiryGroup, setSelectedExpiryGroup] = useState('');
  const [selectedExpiryStatus, setSelectedExpiryStatus] = useState('');
  const [selectedStorageStatus, setSelectedStorageStatus] = useState('');
  const [minQty, setMinQty] = useState('');
  const [maxQty, setMaxQty] = useState('');
  const [minDaysStored, setMinDaysStored] = useState('');
  const [maxDaysStored, setMaxDaysStored] = useState('');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [showFilters, setShowFilters] = useState(true);

  // Fetch data from API on mount (only if props not provided)
  useEffect(() => {
    const loadExpiryData = async () => {
      if (propData && propData.length > 0) {
        setInternalLoading(false);
        return;
      }

      setInternalLoading(true);
      try {
        // Call /api/expiry and /api/customers endpoints in parallel
        const [res, customersRes] = await Promise.all([
          cachedFetch(`${API}/expiry?limit=5000`, { ttl: 300000 }),
          cachedFetch(`${API}/customers?limit=500`, { ttl: 600000 })
        ]);
        const { stats: apiStats, expired, expiring30, customerStats: apiCustomerStats } = res;

        // Store customer stats for accurate counts
        if (apiCustomerStats) {
          setCustomerStats(apiCustomerStats);
        }

        // Set all customers from API
        const customersList = customersRes?.customers || [];
        setAllCustomers(customersList.map((c: any) => c.name).filter(Boolean).sort());

        // Combine expired + expiring30 items and transform field names
        const allItems = [...(expired || []), ...(expiring30 || [])];
        const transformedData = allItems.map((item: any) => ({
          ITEM: item.ITEM_CODE || '',
          ITEM_DESCRIPTION1: item.ITEM_NAME || item.ITEM_DESC || '',
          BATCH_NO: item.BATCH || '',
          COMPANY: item.CUSTOMER || '',
          WAREHOUSE: item.WAREHOUSE || '',
          DAYS_IN_STORAGE: item.DAYS_IN_STORAGE || 0,
          AVAILABLE_QTY: item.QTY || 0,
          RESERVED_QTY: item.RESERVED_QTY || 0,
          EXPIRY_DATE: item.EXPIRY_DATE !== 'No Expiry' ? item.EXPIRY_DATE : null,
          STORAGE_BIN_LOCATION: item.BIN_LOCATION || item.FULL_LOCATION || '',
          EXPIRY_STATUS: item.EXPIRY_STATUS,
          UOM: item.UOM || 'PCS',
          EXPIRY_GROUP: item.EXPIRY_STATUS === 'EXPIRED' ? 'Expired' :
                        item.EXPIRY_STATUS === 'EXPIRING_30_DAYS' ? '0-30 Days' :
                        item.EXPIRY_STATUS === 'EXPIRING_60_DAYS' ? '31-60 Days' :
                        item.EXPIRY_STATUS === 'EXPIRING_90_DAYS' ? '61-90 Days' : 'Other'
        }));
        setInternalData(transformedData);

        // Transform stats
        const transformedStats = {
          TOTAL_ITEMS: apiStats?.TOTAL_ITEMS || transformedData.length,
          TOTAL_BATCHES: new Set(transformedData.map((i: any) => i.BATCH_NO).filter(Boolean)).size,
          TOTAL_AVAILABLE_QTY: transformedData.reduce((sum: number, i: any) => sum + (parseFloat(i.AVAILABLE_QTY) || 0), 0),
          EXPIRED_ITEMS: apiStats?.EXPIRED || transformedData.filter((i: any) => i.EXPIRY_STATUS === 'EXPIRED').length,
          EXPIRING_SOON_90_DAYS: apiStats?.EXPIRING_90_DAYS || transformedData.filter((i: any) => ['EXPIRING_30_DAYS', 'EXPIRING_60_DAYS', 'EXPIRING_90_DAYS'].includes(i.EXPIRY_STATUS)).length
        };
        setInternalStats(transformedStats);

        // Calculate storage age distribution from data
        const storageDist: any = { '0-30 days': 0, '31-90 days': 0, '91-180 days': 0, '180+ days': 0 };
        transformedData.forEach((item: any) => {
          const days = item.DAYS_IN_STORAGE;
          if (days !== null && days !== undefined) {
            if (days <= 30) storageDist['0-30 days']++;
            else if (days <= 90) storageDist['31-90 days']++;
            else if (days <= 180) storageDist['91-180 days']++;
            else storageDist['180+ days']++;
          }
        });
        setInternalStorageAgeDistribution(Object.entries(storageDist).map(([STORAGE_AGE, ITEM_COUNT]) => ({ STORAGE_AGE, ITEM_COUNT })));

        // Calculate expiry distribution from data
        const expiryDist: any = { 'Expired': 0, '0-30 Days': 0, '31-90 Days': 0, '91-180 Days': 0, '180+ Days': 0 };
        transformedData.forEach((item: any) => {
          const status = item.EXPIRY_STATUS;
          if (status === 'EXPIRED') expiryDist['Expired']++;
          else if (status === 'EXPIRING_30_DAYS') expiryDist['0-30 Days']++;
          else if (['EXPIRING_60_DAYS', 'EXPIRING_90_DAYS'].includes(status)) expiryDist['31-90 Days']++;
          else expiryDist['91-180 Days']++;
        });
        setInternalExpiryDistribution(Object.entries(expiryDist).map(([EXPIRY_GROUP, ITEM_COUNT]) => ({ EXPIRY_GROUP, ITEM_COUNT })));

        setInternalLoading(false);
      } catch (error: any) {
        console.error('Error loading expiry data:', error);
        setInternalLoading(false);
      }
    };

    loadExpiryData();
  }, [propData]);

  // Re-fetch data when a specific customer is selected to get ALL their items
  useEffect(() => {
    const loadCustomerData = async () => {
      if (!selectedCompany || propData && propData.length > 0) return;

      setInternalLoading(true);
      try {
        // Fetch data filtered by customer with higher limit
        const res = await cachedFetch(`${API}/expiry?limit=10000&customer=${encodeURIComponent(selectedCompany)}`, { ttl: 60000 });
        const { expired, expiring30, totalExpired, totalExpiring30 } = res;

        // Combine and transform data for this customer
        const allItems = [...(expired || []), ...(expiring30 || [])];
        const transformedData = allItems.map((item: any) => ({
          ITEM: item.ITEM_CODE || '',
          ITEM_DESCRIPTION1: item.ITEM_NAME || item.ITEM_DESC || '',
          BATCH_NO: item.BATCH || '',
          COMPANY: item.CUSTOMER || '',
          WAREHOUSE: item.WAREHOUSE || '',
          DAYS_IN_STORAGE: item.DAYS_IN_STORAGE || 0,
          AVAILABLE_QTY: item.QTY || 0,
          RESERVED_QTY: item.RESERVED_QTY || 0,
          EXPIRY_DATE: item.EXPIRY_DATE !== 'No Expiry' ? item.EXPIRY_DATE : null,
          STORAGE_BIN_LOCATION: item.BIN_LOCATION || item.FULL_LOCATION || '',
          EXPIRY_STATUS: item.EXPIRY_STATUS,
          UOM: item.UOM || 'PCS',
          EXPIRY_GROUP: item.EXPIRY_STATUS === 'EXPIRED' ? 'Expired' :
                        item.EXPIRY_STATUS === 'EXPIRING_30_DAYS' ? '0-30 Days' :
                        item.EXPIRY_STATUS === 'EXPIRING_60_DAYS' ? '31-60 Days' :
                        item.EXPIRY_STATUS === 'EXPIRING_90_DAYS' ? '61-90 Days' : 'Other'
        }));
        setInternalData(transformedData);

        console.log(`✅ Loaded ${transformedData.length} items for ${selectedCompany} (Total: ${totalExpired} expired, ${totalExpiring30} expiring)`);
        setInternalLoading(false);
      } catch (error: any) {
        console.error('Error loading customer data:', error);
        setInternalLoading(false);
      }
    };

    loadCustomerData();
  }, [selectedCompany, propData]);

  // Helper function to determine storage status based on days stored
  const getStorageStatus = (daysStored: number) => {
    if (!daysStored && daysStored !== 0) return { status: 'Unknown', color: '#94a3b8', icon: '❓', category: 'unknown' };
    
    if (daysStored <= 30) {
      return { status: 'Newly Stored', color: '#06b6d4', icon: '🆕', category: 'new', days: daysStored };
    } else if (daysStored <= 90) {
      return { status: 'Recent Storage', color: '#8b5cf6', icon: '📦', category: 'recent', days: daysStored };
    } else if (daysStored <= 180) {
      return { status: 'Aged Storage', color: '#f59e0b', icon: '⏳', category: 'aged', days: daysStored };
    } else {
      return { status: 'Long-Term Storage', color: '#ef4444', icon: '🏚️', category: 'long-term', days: daysStored };
    }
  };

  // Helper function to determine expiry status
  const getExpiryStatus = (expiryDate: string) => {
    if (!expiryDate) return { status: 'No Expiry Date', color: '#94a3b8', icon: '❓', category: 'unknown' };
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'Expired', color: '#dc2626', icon: '❌', category: 'expired', days: Math.abs(daysUntilExpiry) };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'Critical - 30 Days', color: '#ef4444', icon: '🚨', category: 'critical', days: daysUntilExpiry };
    } else if (daysUntilExpiry <= 90) {
      return { status: 'Expiring Soon - 90 Days', color: '#f59e0b', icon: '⚠️', category: 'expiring', days: daysUntilExpiry };
    } else if (daysUntilExpiry <= 180) {
      return { status: 'Monitor - 180 Days', color: '#3b82f6', icon: '⏰', category: 'monitor', days: daysUntilExpiry };
    } else {
      return { status: 'Fresh', color: '#10b981', icon: '✅', category: 'fresh', days: daysUntilExpiry };
    }
  };

  // Calculate storage distribution
  const storageStatusDistribution = useMemo(() => {
    const distribution: any = {
      'Newly Stored (0-30 days)': 0,
      'Recent Storage (31-90 days)': 0,
      'Aged Storage (91-180 days)': 0,
      'Long-Term Storage (180+ days)': 0
    };

    data.forEach(item => {
      const daysStored = parseInt(item.DAYS_IN_STORAGE) || 0;
      if (daysStored <= 30) {
        distribution['Newly Stored (0-30 days)']++;
      } else if (daysStored <= 90) {
        distribution['Recent Storage (31-90 days)']++;
      } else if (daysStored <= 180) {
        distribution['Aged Storage (91-180 days)']++;
      } else {
        distribution['Long-Term Storage (180+ days)']++;
      }
    });

    return Object.entries(distribution).map(([key, value]) => ({
      name: key,
      value: value
    }));
  }, [data]);

  // Calculate UOM distribution
  const uomDistribution = useMemo(() => {
    const distribution: Record<string, { count: number; qty: number }> = {};

    data.forEach(item => {
      const uom = item.UOM || 'PCS';
      if (!distribution[uom]) {
        distribution[uom] = { count: 0, qty: 0 };
      }
      distribution[uom].count++;
      distribution[uom].qty += parseFloat(item.AVAILABLE_QTY) || 0;
    });

    return Object.entries(distribution)
      .map(([uom, data]) => ({
        name: uom,
        count: data.count,
        qty: Math.round(data.qty)
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8); // Top 8 UOMs
  }, [data]);

  // UOM Colors
  const UOM_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#6366f1'];

  // Enhanced filter logic
  const filteredData = useMemo(() => {
    let filtered = data.filter(item => {
      const matchesSearch = !searchTerm || 
        item.ITEM?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ITEM_DESCRIPTION1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.BATCH_NO?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.STORAGE_BIN_LOCATION?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCompany = !selectedCompany || item.COMPANY === selectedCompany;
      const matchesStorageAge = !selectedStorageAge || item.STORAGE_AGE === selectedStorageAge;
      const matchesExpiryGroup = !selectedExpiryGroup || item.EXPIRY_GROUP === selectedExpiryGroup;
      
      let matchesExpiryStatus = true;
      if (selectedExpiryStatus) {
        const status = getExpiryStatus(item.EXPIRY_DATE);
        matchesExpiryStatus = status.category === selectedExpiryStatus;
      }
      
      let matchesStorageStatus = true;
      if (selectedStorageStatus) {
        const daysStored = parseInt(item.DAYS_IN_STORAGE) || 0;
        const status = getStorageStatus(daysStored);
        matchesStorageStatus = status.category === selectedStorageStatus;
      }
      
      const qty = parseFloat(item.AVAILABLE_QTY) || 0;
      const matchesMinQty = !minQty || qty >= parseFloat(minQty);
      const matchesMaxQty = !maxQty || qty <= parseFloat(maxQty);
      
      const daysStored = parseInt(item.DAYS_IN_STORAGE) || 0;
      const matchesMinDays = !minDaysStored || daysStored >= parseInt(minDaysStored);
      const matchesMaxDays = !maxDaysStored || daysStored <= parseInt(maxDaysStored);
      
      return matchesSearch && matchesCompany && matchesStorageAge && 
             matchesExpiryGroup && matchesExpiryStatus && matchesStorageStatus &&
             matchesMinQty && matchesMaxQty && matchesMinDays && matchesMaxDays;
    });

    if (sortField) {
      filtered.sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        const direction = sortDirection === 'asc' ? 1 : -1;
        
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return (aVal - bVal) * direction;
        }
        return String(aVal).localeCompare(String(bVal)) * direction;
      });
    }

    return filtered;
  }, [data, searchTerm, selectedCompany, selectedStorageAge, selectedExpiryGroup, 
      selectedExpiryStatus, selectedStorageStatus, minQty, maxQty, minDaysStored, maxDaysStored, sortField, sortDirection]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Only show companies that exist in the data
  const companies = useMemo(() =>
    Array.from(new Set(data.map(item => item.COMPANY).filter(Boolean))).sort(),
    [data]
  );

  const storageAges = useMemo(() => 
    Array.from(new Set(data.map(item => item.STORAGE_AGE).filter(Boolean))).sort(),
    [data]
  );

  const expiryGroups = useMemo(() => 
    Array.from(new Set(data.map(item => item.EXPIRY_GROUP).filter(Boolean))).sort(),
    [data]
  );

  const avgStorageDuration = useMemo(() => {
    const validItems = data.filter(item => item.DAYS_IN_STORAGE);
    const total = validItems.reduce((sum, item) => sum + (parseInt(item.DAYS_IN_STORAGE) || 0), 0);
    return validItems.length > 0 ? Math.round(total / validItems.length) : 0;
  }, [data]);

  const formatNumber = (num: number) => new Intl.NumberFormat().format(Math.round(num));

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const exportToCSV = () => {
    const headers = ['Item', 'Description', 'Batch No', 'Company', 'UoM', 'Storage Date', 'Days in Storage', 'Storage Status', 'Available Qty', 'Reserved Qty', 'Expiry Date', 'Days Until Expiry', 'Expiry Status', 'Location'];
    const csvData = filteredData.map(item => {
      const storageStatus = getStorageStatus(parseInt(item.DAYS_IN_STORAGE) || 0);
      const expiryStatus = getExpiryStatus(item.EXPIRY_DATE);
      const storageDate = item.STORAGE_DATE || (item.DAYS_IN_STORAGE ? 
        new Date(Date.now() - parseInt(item.DAYS_IN_STORAGE) * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : 'N/A');
      
      return [
        item.ITEM,
        item.ITEM_DESCRIPTION1,
        item.BATCH_NO,
        item.COMPANY,
        item.UOM || 'PCS',
        storageDate,
        item.DAYS_IN_STORAGE,
        storageStatus.status,
        item.AVAILABLE_QTY,
        item.RESERVED_QTY,
        item.EXPIRY_DATE,
        expiryStatus.days || 'N/A',
        expiryStatus.status,
        item.STORAGE_BIN_LOCATION
      ];
    });
    
    const csv = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expiry-storage-data-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCompany('');
    setSelectedStorageAge('');
    setSelectedExpiryGroup('');
    setSelectedExpiryStatus('');
    setSelectedStorageStatus('');
    setMinQty('');
    setMaxQty('');
    setMinDaysStored('');
    setMaxDaysStored('');
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="esd-loading">
        <div className="esd-spinner"></div>
        <p className="esd-loading-text">
          Loading Storage & Expiry Data...
          <br/>
          <span className="esd-loading-subtitle">Analyzing inventory timeline and status</span>
        </p>
      </div>
    );
  }

  return (
    <div className="esd-dashboard">
      {/* TOP BAR */}
      <div className="esd-top-bar">
        <h1 className="esd-top-bar-title">Storage & Expiry Intelligence</h1>
        <div className="esd-top-bar-buttons">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`esd-btn-small ${showFilters ? 'active' : ''}`}
            title="Toggle filters panel"
          >
            {showFilters ? '✕ Filters' : '⊕ Filters'}
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`esd-btn-small ${viewMode === 'table' ? 'active' : ''}`}
            title="Table view"
          >
            📋 Table
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`esd-btn-small ${viewMode === 'grid' ? 'active' : ''}`}
            title="Grid view"
          >
            🎯 Grid
          </button>
          <button
            onClick={exportToCSV}
            className="esd-btn-small esd-btn-export"
            title="Export data to CSV"
          >
            📥 CSV
          </button>
        </div>
      </div>

      {/* KPI STRIP */}
      <div className="esd-kpi-strip">
        <div className="esd-kpi-chip" style={{ borderLeftColor: COLORS.primary }}>
          <div className="esd-kpi-icon">📦</div>
          <div className="esd-kpi-content">
            <div className="esd-kpi-label">Total Items</div>
            <div className="esd-kpi-value">{formatNumber(stats?.TOTAL_ITEMS || 0)}</div>
          </div>
        </div>

        <div className="esd-kpi-chip" style={{ borderLeftColor: COLORS.info }}>
          <div className="esd-kpi-icon">⏱️</div>
          <div className="esd-kpi-content">
            <div className="esd-kpi-label">Avg Storage</div>
            <div className="esd-kpi-value">{avgStorageDuration}d</div>
          </div>
        </div>

        <div className="esd-kpi-chip" style={{ borderLeftColor: COLORS.secondary }}>
          <div className="esd-kpi-icon">🏷️</div>
          <div className="esd-kpi-content">
            <div className="esd-kpi-label">Batches</div>
            <div className="esd-kpi-value">{formatNumber(stats?.TOTAL_BATCHES || 0)}</div>
          </div>
        </div>

        <div className="esd-kpi-chip" style={{ borderLeftColor: COLORS.success }}>
          <div className="esd-kpi-icon">✅</div>
          <div className="esd-kpi-content">
            <div className="esd-kpi-label">Available Qty</div>
            <div className="esd-kpi-value">{formatNumber(stats?.TOTAL_AVAILABLE_QTY || 0)}</div>
          </div>
        </div>

        <div className="esd-kpi-chip" style={{ borderLeftColor: COLORS.warning }}>
          <div className="esd-kpi-icon">⚠️</div>
          <div className="esd-kpi-content">
            <div className="esd-kpi-label">Expiring Soon</div>
            <div className="esd-kpi-value">{formatNumber(stats?.EXPIRING_SOON_90_DAYS || 0)}</div>
          </div>
        </div>

        <div className="esd-kpi-chip" style={{ borderLeftColor: COLORS.danger }}>
          <div className="esd-kpi-icon">❌</div>
          <div className="esd-kpi-content">
            <div className="esd-kpi-label">Expired</div>
            <div className="esd-kpi-value">{formatNumber(stats?.EXPIRED_ITEMS || 0)}</div>
          </div>
        </div>
      </div>

      {/* FILTERS SECTION */}
      {showFilters && (
        <div className="esd-filters-section">
          <div className="esd-filters-row">
            <div className="esd-filter-group">
              <label className="esd-filter-label">Storage Status</label>
              <div className="esd-filter-pills">
                {['Newly Stored', 'Recent Storage', 'Aged Storage', 'Long-Term Storage'].map(status => (
                  <button
                    key={status}
                    onClick={() => setSelectedStorageStatus(selectedStorageStatus === status ? '' : status)}
                    className={`esd-filter-pill ${selectedStorageStatus === status ? 'active' : ''}`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            <div className="esd-filter-group">
              <label className="esd-filter-label">Expiry Status</label>
              <div className="esd-filter-pills">
                {['Expired', 'Critical - 30 Days', 'Expiring Soon - 90 Days', 'Monitor - 180 Days', 'Fresh'].map(status => (
                  <button
                    key={status}
                    onClick={() => setSelectedExpiryStatus(selectedExpiryStatus === status ? '' : status)}
                    className={`esd-filter-pill ${selectedExpiryStatus === status ? 'active' : ''}`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="esd-filters-row">
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="esd-filter-input"
            />

            <select
              value={selectedCompany}
              onChange={(e) => {
                setSelectedCompany(e.target.value);
                setCurrentPage(1);
              }}
              className="esd-filter-select"
            >
              <option value="">All Companies</option>
              {companies.map(company => (
                <option key={company} value={company}>{company}</option>
              ))}
            </select>

            <input
              type="number"
              placeholder="Min Qty"
              value={minQty}
              onChange={(e) => {
                setMinQty(e.target.value);
                setCurrentPage(1);
              }}
              className="esd-filter-input"
            />

            <input
              type="number"
              placeholder="Max Qty"
              value={maxQty}
              onChange={(e) => {
                setMaxQty(e.target.value);
                setCurrentPage(1);
              }}
              className="esd-filter-input"
            />

            <button onClick={resetFilters} className="esd-btn-reset">
              Reset Filters
            </button>
          </div>

          <div className="esd-filter-count">
            {filteredData.length} items match your filters
          </div>
        </div>
      )}

      {/* ANALYTICS GRID */}
      {data.length > 0 && (
        <div className="esd-analytics">
          {/* Storage Age Distribution */}
          <div className="esd-chart-card">
            <h3 className="esd-chart-title">📊 Storage Age Distribution</h3>
            {storageAgeDistribution && storageAgeDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={storageAgeDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="STORAGE_AGE" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0' }}
                    labelStyle={{ color: '#1e293b' }}
                  />
                  <Bar dataKey="ITEM_COUNT" fill={COLORS.primary} radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : null}
          </div>

          {/* Expiry Timeline */}
          <div className="esd-chart-card">
            <h3 className="esd-chart-title">⏰ Expiry Timeline</h3>
            {expiryDistribution && expiryDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={expiryDistribution}
                    dataKey="ITEM_COUNT"
                    nameKey="EXPIRY_GROUP"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {expiryDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={EXPIRY_COLORS[index % EXPIRY_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: any) => formatNumber(Number(value))} />
                </PieChart>
              </ResponsiveContainer>
            ) : null}
          </div>

          {/* Top Customers */}
          <div className="esd-chart-card">
            <h3 className="esd-chart-title">👥 Top Customers by Items</h3>
            {Object.entries(customerStats).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  layout="vertical"
                  data={Object.entries(customerStats)
                    .map(([name, stats]: any) => ({
                      name,
                      count: (stats.expired || 0) + (stats.expiring30 || 0)
                    }))
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 10)}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" stroke="#64748b" />
                  <YAxis dataKey="name" type="category" stroke="#64748b" width={150} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0' }}
                    labelStyle={{ color: '#1e293b' }}
                  />
                  <Bar dataKey="count" fill={COLORS.secondary} radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : null}
          </div>

          {/* Storage Duration vs Quantity */}
          <div className="esd-chart-card">
            <h3 className="esd-chart-title">📈 Storage Duration vs Quantity</h3>
            {filteredData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={filteredData.slice(0, 30)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="ITEM" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0' }}
                    labelStyle={{ color: '#1e293b' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="AVAILABLE_QTY" 
                    fill={COLORS.success} 
                    stroke={COLORS.success}
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : null}
          </div>
        </div>
      )}

      {/* RESULTS BAR */}
      <div className="esd-results-bar">
        <span className="esd-results-count">
          Showing {paginatedData.length} of {filteredData.length} items
        </span>
        <div className="esd-results-controls">
          <label className="esd-results-label">
            Per page:
            <select 
              value={itemsPerPage}
              className="esd-items-per-page-select"
              disabled
            >
              <option>50</option>
            </select>
          </label>
        </div>
      </div>

      {/* DATA SECTION */}
      <div className="esd-data-section">
        {filteredData.length === 0 ? (
          <div className="esd-no-data">
            <div className="esd-no-data-icon">🔍</div>
            <p className="esd-no-data-text">No items match your filters</p>
          </div>
        ) : viewMode === 'table' ? (
          <div className="esd-table-wrapper">
            <table className="esd-data-table">
              <thead>
                <tr className="esd-table-header">
                  <th className="esd-table-th">Item</th>
                  <th className="esd-table-th">Description</th>
                  <th className="esd-table-th">Batch</th>
                  <th className="esd-table-th">Company</th>
                  <th className="esd-table-th">Days Stored</th>
                  <th className="esd-table-th">Storage Status</th>
                  <th className="esd-table-th">Qty</th>
                  <th className="esd-table-th">Expiry Date</th>
                  <th className="esd-table-th">Expiry Status</th>
                  <th className="esd-table-th">Location</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, idx) => {
                  const storageStatus = getStorageStatus(parseInt(item.DAYS_IN_STORAGE) || 0);
                  const expiryStatus = getExpiryStatus(item.EXPIRY_DATE);
                  return (
                    <tr key={idx} className={`esd-table-row ${idx % 2 === 0 ? 'even' : 'odd'}`}>
                      <td className="esd-table-td esd-td-item">{item.ITEM}</td>
                      <td className="esd-table-td esd-td-desc">{item.ITEM_DESCRIPTION1}</td>
                      <td className="esd-table-td">{item.BATCH_NO}</td>
                      <td className="esd-table-td">{item.COMPANY}</td>
                      <td className="esd-table-td esd-td-right">{item.DAYS_IN_STORAGE}</td>
                      <td className="esd-table-td">
                        <span className="esd-status-badge" style={{ backgroundColor: storageStatus.color }}>
                          {storageStatus.icon} {storageStatus.status}
                        </span>
                      </td>
                      <td className="esd-table-td esd-td-right">{formatNumber(item.AVAILABLE_QTY)}</td>
                      <td className="esd-table-td">{item.EXPIRY_DATE || 'N/A'}</td>
                      <td className="esd-table-td">
                        <span className="esd-status-badge" style={{ backgroundColor: expiryStatus.color }}>
                          {expiryStatus.icon} {expiryStatus.status}
                        </span>
                      </td>
                      <td className="esd-table-td esd-td-location">{item.STORAGE_BIN_LOCATION}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="esd-grid-view">
            {paginatedData.map((item, idx) => {
              const storageStatus = getStorageStatus(parseInt(item.DAYS_IN_STORAGE) || 0);
              const expiryStatus = getExpiryStatus(item.EXPIRY_DATE);
              return (
                <div key={idx} className="esd-grid-card">
                  <div className="esd-grid-card-badges">
                    <span className="esd-grid-badge" style={{ backgroundColor: storageStatus.color }}>
                      {storageStatus.status}
                    </span>
                    <span className="esd-grid-badge" style={{ backgroundColor: expiryStatus.color }}>
                      {expiryStatus.status}
                    </span>
                  </div>
                  <div className="esd-grid-card-content">
                    <h4 className="esd-grid-item-name">{item.ITEM}</h4>
                    <p className="esd-grid-item-desc">{item.ITEM_DESCRIPTION1}</p>
                    <div className="esd-grid-card-meta">
                      <div className="esd-grid-meta-row">
                        <span className="esd-grid-meta-label">Batch:</span>
                        <span className="esd-grid-meta-value">{item.BATCH_NO}</span>
                      </div>
                      <div className="esd-grid-meta-row">
                        <span className="esd-grid-meta-label">Company:</span>
                        <span className="esd-grid-meta-value">{item.COMPANY}</span>
                      </div>
                      <div className="esd-grid-meta-row">
                        <span className="esd-grid-meta-label">Days Stored:</span>
                        <span className="esd-grid-meta-value">{item.DAYS_IN_STORAGE}</span>
                      </div>
                      <div className="esd-grid-meta-row">
                        <span className="esd-grid-meta-label">Available Qty:</span>
                        <span className="esd-grid-meta-value">{formatNumber(item.AVAILABLE_QTY)}</span>
                      </div>
                      <div className="esd-grid-meta-row">
                        <span className="esd-grid-meta-label">Expiry Date:</span>
                        <span className="esd-grid-meta-value">{item.EXPIRY_DATE || 'No Expiry'}</span>
                      </div>
                      <div className="esd-grid-meta-row">
                        <span className="esd-grid-meta-label">Location:</span>
                        <span className="esd-grid-meta-value">{item.STORAGE_BIN_LOCATION}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="esd-pagination">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className={`esd-btn-paginate ${currentPage === 1 ? 'disabled' : ''}`}
          >
            ← Prev
          </button>
          <span className="esd-pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className={`esd-btn-paginate ${currentPage === totalPages ? 'disabled' : ''}`}
          >
            Next →
          </button>
        </div>
      )}
    </div>

  );
};

export default ExpiryStoreDashboard;
